import React, { useEffect, useState } from 'react';
import MiddlePanel from 'Components/MiddlePanel/MiddlePanel';
import Panel from 'Components/Panel/Panel';
import Button from 'Components/Button/Button';
import ComboBox from 'Components/ComboBox/ComboBox';
import InputField from 'Components/Input/InputField';
import MandatoryIconField from 'Components/MandatoryIconField';
import UploadButton from 'Components/UploadButton/UploadButton';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import ExecuteAxios from 'Components/ExecuteAxios/ExecuteAxios';
import OptionsDataByAttributePath from 'Components/Options/optionsData/OptionsDataByAttributePath';
import PanelListDefaultGridEditable from 'Components/PanelListGridEditable/PanelListDefaultGridEditable';
import { getFromLocalStorage, saveToLocalStorage, clearLocalStorage } from 'Components/AutomaticallyListedAttributesGrid/AutomaticallyListedAttributesGrid';
import './CreateProductFromDocument.css';

const LOCAL_STORAGE_NAME = 'technicalSheetOperationList';

// Colunas do grid editável (mesmo formato { id, constructorList } que o backend devolve para as views)
const GRID_COLUMNS = [
    { field: 'id', labelGrid: 'Sel.', columnType: 'checkbox', readOnly: 'N' },
    { field: 'operationSequence', labelGrid: 'Seq.', columnType: 'input', readOnly: 'S' },
    { field: 'dsOperation', labelGrid: 'Operação', columnType: 'input', readOnly: 'S' },
    { field: 'operationStatus', labelGrid: 'Operação no fornecedor', columnType: 'input', readOnly: 'S' },
    { field: 'dsMachine', labelGrid: 'Tipo de máquina', columnType: 'input', readOnly: 'S' },
    { field: 'machineStatus', labelGrid: 'Máquina', columnType: 'input', readOnly: 'S' },
    { field: 'timePerOperation', labelGrid: 'Tempo padrão', columnType: 'input', readOnly: 'N' }
];

const attributeList = [
    { name: 'id', minGridWidth: '50px', maxGridWidth: '50px' },
    { name: 'operationSequence', minGridWidth: '60px', maxGridWidth: '60px' },
    { name: 'dsOperation', minGridWidth: '380px', maxGridWidth: '380px', whiteSpace: 'normal' },
    { name: 'operationStatus', minGridWidth: '150px', maxGridWidth: '150px' },
    { name: 'dsMachine', minGridWidth: '170px', maxGridWidth: '170px' },
    { name: 'machineStatus', minGridWidth: '90px', maxGridWidth: '90px' },
    { name: 'timePerOperation', minGridWidth: '110px', maxGridWidth: '110px', decimalScale: 4 }
];

function getStatusDescription(exists) {
    return exists === 'S' ? 'Existente' : 'Nova';
}

function operationsToRows(operations) {
    return operations.map((operation, index) => {
        const values = {
            id: index + 1,
            operationSequence: operation.operationSequence,
            dsOperation: operation.dsOperation,
            operationStatus: getStatusDescription(operation.operationExists),
            dsMachine: operation.dsMachine,
            machineStatus: operation.dsMachine ? getStatusDescription(operation.machineExists) : '',
            timePerOperation: operation.timePerOperation
        };

        return {
            id: index + 1,
            constructorList: GRID_COLUMNS.map((column, columnIndex) => ({
                ...column,
                presentationSequence: columnIndex + 1,
                visibleTitle: 'S',
                argument: values[column.field],
                checked: column.columnType === 'checkbox' ? operation.checked : null
            }))
        };
    });
}

function getRowAttribute(row, field) {
    return row.constructorList.find(item => item.field === field);
}

const CreateProductFromDocument = ({
    isOpen,
    setIsOpen,
    onProductCreated
}) => {
    const [supplierList, setSupplierList] = useState(null);
    const [supplierId, setSupplierId] = useState(null);
    const [file, setFile] = useState(null);
    const [sheet, setSheet] = useState(null);
    const [operationRows, setOperationRows] = useState(null);
    const [loading, setLoading] = useState(false);

    const itemsSupplierOptions = OptionsDataByAttributePath(supplierList, ['description'], null, true);
    const selectedRows = operationRows ? operationRows.filter(row => getRowAttribute(row, 'id').checked === 'S') : [];
    const totalTime = selectedRows.reduce((sum, row) => sum + (Number(getRowAttribute(row, 'timePerOperation').argument) || 0), 0);

    useEffect(() => {
        if (isOpen && !supplierList) {
            callToSetSupplierList();
        }
        clearLocalStorage(LOCAL_STORAGE_NAME);
    }, [isOpen]);

    // O fornecedor define quais operações já existem, então relemos o documento ao trocá-lo
    useEffect(() => {
        if (!file) {
            return;
        }
        if (supplierId) {
            readDocument(file);
        } else {
            resetDocument();
        }
    }, [supplierId]);

    async function callToSetSupplierList() {
        const list = await ExecuteAxios.api('get', `/supplier/get-active-supplier-list/`);
        setSupplierList(list);
    }

    async function readDocument(selectedFile, keepUserEdits = true) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('supplierId', supplierId);

        try {
            setLoading(true);
            const response = await ExecuteAxios.api('post', `/product/read-technical-sheet/`, formData);
            if (!response) {
                return;
            }

            const previousRows = keepUserEdits ? getFromLocalStorage(LOCAL_STORAGE_NAME) : [];
            const rows = operationsToRows(response.operations);

            // Ao reler o mesmo documento (troca de fornecedor) preserva o que o usuário já marcou/editou
            previousRows.forEach((previous, index) => {
                if (!rows[index]) {
                    return;
                }
                getRowAttribute(rows[index], 'id').checked = getRowAttribute(previous, 'id').checked;
                getRowAttribute(rows[index], 'timePerOperation').argument = getRowAttribute(previous, 'timePerOperation').argument;
            });

            setSheet(previous => (keepUserEdits && previous ? { ...response, ...pickProductFields(previous) } : response));
            saveToLocalStorage(LOCAL_STORAGE_NAME, setOperationRows, null, rows);
        } finally {
            setLoading(false);
        }
    }

    function pickProductFields(source) {
        return {
            productCode: source.productCode,
            dsProduct: source.dsProduct,
            dsCloth: source.dsCloth,
            collection: source.collection
        };
    }

    function onUploadDocument(selectedFile) {
        setFile(selectedFile);
        readDocument(selectedFile, false);
    }

    function updateSheet(field, value) {
        setSheet(previous => ({ ...previous, [field]: value }));
    }

    function resetDocument() {
        setFile(null);
        setSheet(null);
        setOperationRows(null);
        clearLocalStorage(LOCAL_STORAGE_NAME);
    }

    function close() {
        setIsOpen(false);
        setSupplierId(null);
        resetDocument();
    }

    function mappedOperations() {
        const rows = getFromLocalStorage(LOCAL_STORAGE_NAME);
        return sheet.operations.map((operation, index) => {
            const row = rows[index];
            return {
                ...operation,
                checked: getRowAttribute(row, 'id').checked,
                timePerOperation: getRowAttribute(row, 'timePerOperation').argument || 0
            };
        });
    }

    async function createProduct() {
        if (!sheet.productCode) {
            alert('Informe o código do produto.');
            return;
        }
        if (selectedRows.length === 0) {
            alert('Selecione ao menos uma operação.');
            return;
        }

        try {
            setLoading(true);
            const operations = mappedOperations();
            const productId = await ExecuteAxios.api('post', `/product/create-from-technical-sheet/`, {
                ...sheet,
                supplierId: supplierId,
                operations: operations
            });

            if (productId) {
                const checkedOperations = operations.filter(item => item.checked === 'S');
                const newOperations = checkedOperations.filter(item => item.operationExists !== 'S').length;
                alert(`Produto ${sheet.productCode} criado com ${checkedOperations.length} operações (${newOperations} novas no fornecedor).`);
                if (onProductCreated) {
                    onProductCreated(sheet.productCode);
                }
                close();
            }
        } finally {
            setLoading(false);
        }
    }

    function getProductFields() {
        const fields = [
            { name: 'productCode', label: 'Código do produto', width: '160px', isMandatory: true },
            { name: 'dsProduct', label: 'Descrição', width: '320px' },
            { name: 'dsCloth', label: 'Tecido', width: '180px' },
            { name: 'collection', label: 'Coleção', width: '180px' }
        ];

        return (
            <div className="create-product-doc-row">
                {fields.map(field => (
                    <div key={field.name} className="create-product-doc-field">
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <span className="span-operation-filter">{field.label}</span>
                            {field.isMandatory && <MandatoryIconField />}
                        </div>
                        <InputField
                            setAttribute={(value) => updateSheet(field.name, value)}
                            value={sheet[field.name] || ''}
                            style={{ width: field.width }}
                        />
                    </div>
                ))}
            </div>
        );
    }

    function getContent() {
        if (!sheet || !operationRows) {
            return (
                <div className="create-product-doc-empty">
                    {supplierId
                        ? 'Selecione o documento da ficha técnica (PDF). As operações do bloco "Sequência operacional" serão lidas para conferência antes de criar o produto.'
                        : 'Selecione o fornecedor para escolher o documento.'}
                </div>
            );
        }

        return (
            <>
                {getProductFields()}
                <PanelListDefaultGridEditable
                    rowsBackend={operationRows}
                    setRowsBackend={setOperationRows}
                    localStorageName={LOCAL_STORAGE_NAME}
                    attributeList={attributeList}
                    height={'calc(100% - 175px)'}
                />
                <div className="create-product-doc-footer">
                    <span>
                        {selectedRows.length} de {operationRows.length} operações selecionadas · Tempo total: {totalTime.toFixed(4).replace('.', ',')} min
                    </span>
                    <Button
                        clickMethod={createProduct}
                        buttonName={'Criar produto'}
                        disabled={selectedRows.length === 0}
                        style={{ color: 'green', fontWeight: '600', fontSize: '0.8em', height: '30px', padding: '0px 20px' }}
                    />
                </div>
            </>
        );
    }

    return (
        <MiddlePanel isVisible={isOpen} zIndex={100} overlay={true}>
            <Panel
                titlePage={'Escanear sequência operacional'}
                canViewCRUD={true}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '1100px',
                    height: '640px',
                    padding: '5px'
                }}
                componentRightButtons={
                    <Button
                        clickMethod={close}
                        buttonName={'Fechar'}
                        style={{ padding: '0px 20px', color: 'red', fontWeight: '600', fontSize: '0.8em', height: '30px' }}
                    />
                }
            >
                <div className="create-product-doc">
                    <div className="create-product-doc-row">
                        <div className="create-product-doc-field">
                            <div style={{ display: 'flex', flexDirection: 'row' }}>
                                <span className="span-operation-filter">Fornecedor</span>
                                <MandatoryIconField />
                            </div>
                            <ComboBox
                                setAttribute={setSupplierId}
                                optionsData={itemsSupplierOptions}
                                value={supplierId}
                                defaultValue={supplierId}
                                style={{ width: '260px' }}
                            />
                        </div>
                        <UploadButton
                            uploadMethod={onUploadDocument}
                            buttonName={file ? 'Trocar documento' : 'Selecionar documento (PDF)'}
                            accept="application/pdf,.pdf"
                            disabled={!supplierId}
                            title={!supplierId ? 'Selecione o fornecedor primeiro' : undefined}
                            style={{ float: 'none', alignSelf: 'flex-end' }}
                        />
                    </div>

                    <LoadingIndicator loaded={!loading}>
                        {getContent()}
                    </LoadingIndicator>
                </div>
            </Panel>
        </MiddlePanel>
    );
};

export default CreateProductFromDocument;
